import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";
import { User } from './user';
import { Place } from './place';
import { EmployeeView, EmployeeViewProxy } from '../views/employee';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { UserType } from '../types';
import { PlaceType } from '../types';

const _schema = {
    "name": "STAFF",
    "fields": [
        { name: "status", type: "string", default: "AC" },
        { name: "place", type: "objid" },
        { name: "user", type: "objid" },
        { name: "alias", type: "string" },
        { name: "allow", type: "string" },
        { name: "email", type: "string" },
        { name: "photo", type: "file" },
    ],
    relate: [
        { direction: ">", target: "USER", by: "user", name: "user", reason: "user", child: false, class: UserType },
        { direction: ">", target: "PLACE", by: "place", name: "place", reason: "place", child: false, class: PlaceType },
    ]
} as const;

const _EmployeeClass = CreateObject(_schema);
if (!_EmployeeClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export enum EmployeePermission {
    StaffManage = 0,
    OfferManage = 1,
    ProductManage = 2,
    TableManage = 3,
    ExtraManage = 4, 
    PlaceManage = 5,
    LicenceManage = 6,
    CanCompliment = 7,
    CanAvailable = 8,
    POSManage = 9,
    ReportAccess = 10,
    TillManage = 11,
    TillOpen = 12,
    CanReopen = 13,
    CanModify = 14,
    CanCancel = 15,
    CanReturn = 16,
    CanAccounts = 17,
    LicenceView = 18,
    QrOpenManage = 19,
};

export class Employee extends _EmployeeClass { 
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): Employee {
        return this._Copy(store) as Employee;
    }

    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf || !this.Commited){
            return;
        }

        if (relation === 'place' && related instanceof Place){
            if (related === this.place){
                related.AddEmployee(this);
            }
            return;
        }

        if (relation === 'user' && related instanceof User){
            if (related === this.user){
                related.AddEmployee(this);
            }
        }
    }

    override get viewProxy(): EmployeeView {
        return EmployeeViewProxy(this, this.data);
    }

    /****************************/
    /* FLAGS MANAGEMENT         */
    /****************************/

    protected get flagArray(): Array<boolean> {
        const _array: boolean[] = new Array(Object.keys(EmployeePermission).length / 2).fill(false);
        
        if (this.allow){
            const _flags = Array.from(this.allow);
            for(let i=0; i < _flags.length; i++){
                _array[i] = (_flags[i] == '1');
            }        
        }

        return _array;
    }

    protected set flagArray(value: Array<boolean>) {
        const _array: string[] = [];
        for(let i=0; i < value.length; i++){
            _array[i] = (value[i] === true) ? '1' : '0';
        }

        this.allow = _array.join(''); 
    }

    protected _setFlagValue(flag: EmployeePermission, value: boolean){
        const _flags = this.flagArray;
        _flags[flag] = value;
        this.flagArray = _flags;        
    }

    IsAllowedTo(permission: EmployeePermission) : boolean {
        return this.flagArray[permission];        
    }

    SetAllowTo(permission: EmployeePermission, value: boolean){
        this._setFlagValue(permission, value);
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get status(): string | null {
        return this['__base__status']
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;     // cannot modify deleted items
        }

        if (this.status == value){
            return;
        }

        this['__base__status'] = value;
        if (this.place){
            if ((this.status == 'DE') && !this.Commited){
                this.place.DelEmployee(this);
            }
            else {
                this.place.DoRefresh(this.table);
            }    
        }
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get firstname(): string | null {
        return this.user?.firstname || null;
    }

    get lastname(): string | null {
        return this.user?.lastname || null;
    }

    get fullname(): string | null {
        return this.user?.fullname || null;
    }

    get phone(): string | null {
        return this.user?.phone || null;
    }

    get IsValid(): boolean {
        return (!!this.status && (this.status != 'DE'));
    }
}

